import type { Dispatch, SetStateAction } from 'react'
import type {
  ToolCallStartEvent,
  ToolCallArgsEvent,
  ToolCallEndEvent,
  ToolCallResultEvent,
  TextMessageContentEvent,
  TextMessageEndEvent,
  ReasoningMessageContentEvent,
  ReasoningMessageEndEvent,
  RunFinishedEvent,
  RunErrorEvent,
} from '@ag-ui/client'
import type {
  AssistantMessageDisplay,
  ThinkingMessageDisplay,
  ToolCallDisplay,
} from '../types'
import { toolResultStatus } from '../utils/toolConfig'
import type { AgentStreamAction, StreamingState } from './useAgentStreamReducer'

export function createStreamHandlers(
  dispatch: Dispatch<AgentStreamAction>,
  setStreamingState: Dispatch<SetStateAction<StreamingState>>,
  handleError: (errorMsg: string) => void,
  onStreamComplete?: () => void,
  signal?: AbortSignal,
  onRunStarted?: () => void
) {
  // Mutable tracking state (scoped to one sendMessage call)
  let currentAssistantEventId: string | undefined
  let currentThinkingEventId: string | undefined
  const toolCallIdMap = new Map<string, string>()
  // RUN_FINISHED or RUN_ERROR seen. A stream that closes without either (run
  // stopped from another tab, or dead before the adapter started) needs the
  // caller to hand over to polling.
  let sawTerminal = false

  return {
    get sawTerminal() {
      return sawTerminal
    },

    // RUN_STARTED means the server has upserted the row with
    // run_status='running', so a conversations-list refetch from here on sees
    // it and starts the sidebar polling/badge.
    onRunStartedEvent: () => {
      if (signal?.aborted) return
      onRunStarted?.()
    },

    onTextMessageContentEvent: ({
      event,
      textMessageBuffer,
    }: {
      event: TextMessageContentEvent
      textMessageBuffer: string
    }) => {
      if (signal?.aborted) return
      // textMessageBuffer is the accumulated content *before* this delta is
      // appended. On the very first TEXT_MESSAGE_CONTENT event it is always
      // empty, so combine with the event delta to get the actual content.
      const content = textMessageBuffer + (event.delta || '')

      // Don't create an empty assistant message (e.g. before thinking starts)
      if (!currentAssistantEventId && !content.trim()) return

      if (!currentAssistantEventId) {
        // Create new assistant message event
        currentAssistantEventId = `assistant-${Date.now()}`
        const assistantEvent: AssistantMessageDisplay = {
          id: currentAssistantEventId,
          type: 'assistant_message',
          content,
          timestamp: new Date(),
          isStreaming: true,
        }

        dispatch({ type: 'ADD_EVENT', event: assistantEvent })
        setStreamingState((prev) => ({
          ...prev,
          currentAssistantEventId,
        }))
      } else {
        // Update existing assistant message event
        dispatch({
          type: 'UPDATE_ASSISTANT',
          id: currentAssistantEventId,
          content,
          isStreaming: true,
        })
      }
    },

    onTextMessageEndEvent: ({
      textMessageBuffer,
    }: {
      event: TextMessageEndEvent
      textMessageBuffer: string
    }) => {
      if (signal?.aborted) return
      // Capture the ID before it might be changed
      const eventIdToUpdate = currentAssistantEventId

      if (eventIdToUpdate) {
        dispatch({
          type: 'UPDATE_ASSISTANT',
          id: eventIdToUpdate,
          content: textMessageBuffer || '',
          isStreaming: false,
        })
      }

      // Reset for potential next text phase
      currentAssistantEventId = undefined
    },

    onToolCallStartEvent: ({ event }: { event: ToolCallStartEvent }) => {
      if (signal?.aborted) return
      const displayId = `tool-${event.toolCallId}`
      toolCallIdMap.set(event.toolCallId, displayId)

      const toolName = event.toolCallName || 'unknown_tool'

      const toolEvent: ToolCallDisplay = {
        id: displayId,
        type: 'tool_call',
        toolName,
        toolCallId: event.toolCallId,
        status: 'running',
        timestamp: new Date(),
        startTime: new Date(),
      }

      dispatch({ type: 'ADD_EVENT', event: toolEvent })
    },

    onToolCallArgsEvent: ({
      event,
      toolCallName,
      partialToolCallArgs,
    }: {
      event: ToolCallArgsEvent
      toolCallBuffer: string
      toolCallName: string
      partialToolCallArgs: Record<string, unknown>
    }) => {
      if (signal?.aborted) return
      const displayId = toolCallIdMap.get(event.toolCallId)
      if (!displayId) return

      dispatch({
        type: 'UPDATE_TOOL_CALL',
        id: displayId,
        patch: {
          toolName: toolCallName,
          args: partialToolCallArgs,
        },
      })
    },

    onToolCallResultEvent: ({ event }: { event: ToolCallResultEvent }) => {
      if (signal?.aborted) return
      const displayId = toolCallIdMap.get(event.toolCallId)
      if (!displayId) return

      dispatch({
        type: 'UPDATE_TOOL_CALL',
        id: displayId,
        patch: {
          result: event.content,
          status: toolResultStatus(event.content),
        },
      })
    },

    onToolCallEndEvent: ({
      event,
      toolCallName,
      toolCallArgs,
    }: {
      event: ToolCallEndEvent
      toolCallName: string
      toolCallArgs: Record<string, unknown>
    }) => {
      if (signal?.aborted) return
      const displayId = toolCallIdMap.get(event.toolCallId)
      if (!displayId) return

      dispatch({
        type: 'UPDATE_TOOL_CALL',
        id: displayId,
        patch: {
          toolName: toolCallName,
          args: toolCallArgs,
          status: 'completed' as const,
          endTime: new Date(),
        },
      })
    },

    onReasoningMessageContentEvent: ({
      event,
      reasoningMessageBuffer,
    }: {
      event: ReasoningMessageContentEvent
      reasoningMessageBuffer: string
    }) => {
      if (signal?.aborted) return
      // reasoningMessageBuffer is the accumulated content *before* this delta
      // is appended — same pre-delta convention as textMessageBuffer.
      const content = reasoningMessageBuffer + (event.delta || '')

      if (!currentThinkingEventId) {
        currentThinkingEventId = `thinking-${Date.now()}`
        const thinkingEvent: ThinkingMessageDisplay = {
          id: currentThinkingEventId,
          type: 'thinking_message',
          content,
          timestamp: new Date(),
          isStreaming: true,
        }
        dispatch({ type: 'ADD_EVENT', event: thinkingEvent })
      } else {
        dispatch({
          type: 'UPDATE_THINKING',
          id: currentThinkingEventId,
          content,
          isStreaming: true,
        })
      }
    },

    onReasoningMessageEndEvent: ({
      reasoningMessageBuffer,
    }: {
      event: ReasoningMessageEndEvent
      reasoningMessageBuffer: string
    }) => {
      if (signal?.aborted) return
      const eventIdToUpdate = currentThinkingEventId
      if (eventIdToUpdate) {
        dispatch({
          type: 'UPDATE_THINKING',
          id: eventIdToUpdate,
          content: reasoningMessageBuffer || '',
          isStreaming: false,
        })
      }
      currentThinkingEventId = undefined
    },

    onRunFinishedEvent: (_: { event: RunFinishedEvent }) => {
      if (signal?.aborted) return
      sawTerminal = true
      onStreamComplete?.()
      dispatch({ type: 'COMPLETE_STREAM' })
      setStreamingState({ isStreaming: false })
    },

    onRunErrorEvent: ({ event }: { event: RunErrorEvent }) => {
      if (signal?.aborted) return
      sawTerminal = true
      handleError(event.message || 'An error occurred')
    },
  }
}
